import { computed } from 'vue'
import type { SalesOrdersState } from './state'
import type { SalesOrderStatus } from '@/types'

export const createGetters = (state: SalesOrdersState) => {
  const filteredSalesOrders = computed(() => {
    let result = [...state.salesOrders]

    // Filter by search term
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (o) =>
          o.orderNumber.toLowerCase().includes(search) ||
          o.notes?.toLowerCase().includes(search)
      )
    }

    // Filter by status
    if (state.filters.status) {
      result = result.filter((o) => o.status === state.filters.status)
    }

    // Filter by organization
    if (state.filters.organizationId) {
      result = result.filter((o) => o.organizationId === state.filters.organizationId)
    }

    return result
  })

  const ordersByStatus = computed(() => {
    const grouped: Record<SalesOrderStatus, typeof state.salesOrders> = {
      draft: [],
      sent: [],
      accepted: [],
      rejected: [],
      invoiced: [],
      paid: [],
      cancelled: [],
    }

    state.salesOrders.forEach((o) => {
      if (grouped[o.status]) {
        grouped[o.status].push(o)
      }
    })

    return grouped
  })

  const draftOrders = computed(() => {
    return state.salesOrders.filter((o) => o.status === 'draft')
  })

  const sentOrders = computed(() => {
    return state.salesOrders.filter((o) => o.status === 'sent')
  })

  const acceptedOrders = computed(() => {
    return state.salesOrders.filter((o) => o.status === 'accepted')
  })

  const paidOrders = computed(() => {
    return state.salesOrders.filter((o) => o.status === 'paid')
  })

  const totalRevenue = computed(() => {
    return state.salesOrders
      .filter((o) => o.status === 'paid')
      .reduce((sum, o) => sum + o.total, 0)
  })

  const totalPending = computed(() => {
    return state.salesOrders
      .filter((o) => o.status === 'accepted' || o.status === 'invoiced')
      .reduce((sum, o) => sum + o.total, 0)
  })

  const averageOrderValue = computed(() => {
    const validOrders = state.salesOrders.filter(
      (o) => o.status !== 'draft' && o.status !== 'cancelled' && o.status !== 'rejected'
    )
    if (validOrders.length === 0) return 0
    return validOrders.reduce((sum, o) => sum + o.total, 0) / validOrders.length
  })

  return {
    filteredSalesOrders,
    ordersByStatus,
    draftOrders,
    sentOrders,
    acceptedOrders,
    paidOrders,
    totalRevenue,
    totalPending,
    averageOrderValue,
  }
}
